import { useState, useEffect, useRef, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, Calendar, Users, MapPin, Navigation, Loader2 } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import type { Band } from '../../types/database';
import { getInitials } from '../../lib/utils';
import { useGeolocation } from '../../hooks/useGeolocation';

export default function HomePage() {
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState<Band[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const navigate = useNavigate();
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const { loading: geoLoading, error: geoError, requestLocation } = useGeolocation();
  const [geoErrorVisible, setGeoErrorVisible] = useState(false);

  async function handleNearMe() {
    const coords = await requestLocation();
    if (coords) {
      navigate(`/search?lat=${coords.latitude}&lng=${coords.longitude}`);
    } else {
      setGeoErrorVisible(true);
      setTimeout(() => setGeoErrorVisible(false), 5000);
    }
  }

  // Debounced live search
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!query.trim()) {
      setSuggestions([]);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      const q = query.trim();
      const { data } = await supabase
        .from('bands')
        .select('id, name, slug, logo_url, location_name, location_country, genres')
        .eq('is_public', true)
        .or(`name.ilike.%${q}%,location_name.ilike.%${q}%,location_country.ilike.%${q}%`)
        .order('name')
        .limit(5);
      setSuggestions((data as Band[]) ?? []);
      setShowSuggestions(true);
    }, 250);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [query]);

  // Close suggestions on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setShowSuggestions(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  function handleSearch(e: FormEvent) {
    e.preventDefault();
    if (query.trim()) {
      setShowSuggestions(false);
      navigate(`/search?q=${encodeURIComponent(query.trim())}`);
    }
  }

  return (
    <div>
      {/* Hero */}
      <section className="relative py-24 px-4 text-center">
        <div className="max-w-3xl mx-auto">
          <h1 className="text-5xl md:text-6xl font-bold mb-6">
            Discover Live Music<br />
            <span className="text-indigo-400">Near You</span>
          </h1>
          <p className="text-xl text-gray-400 mb-10">
            Find bands, explore gigs, and never miss a show. Bands: manage your calendar,
            share your schedule, and connect with your audience.
          </p>

          {/* Search bar with live suggestions */}
          <div ref={wrapperRef} className="relative max-w-xl mx-auto">
            <form onSubmit={handleSearch}>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
                    placeholder="Search bands, cities, countries..."
                    className="w-full bg-gray-800 border border-gray-700 rounded-lg pl-12 pr-4 py-3 text-gray-50 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 text-lg"
                  />
                </div>
                <button
                  type="submit"
                  className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-3 rounded-lg font-medium transition"
                >
                  Search
                </button>
              </div>
            </form>

            {/* Near Me button */}
            <div className="flex justify-center mt-3">
              <button
                onClick={handleNearMe}
                disabled={geoLoading}
                className="flex items-center gap-2 text-sm text-indigo-400 hover:text-indigo-300 transition disabled:opacity-50"
              >
                {geoLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Navigation className="w-4 h-4" />
                )}
                {geoLoading ? 'Getting your location...' : 'Find bands near me'}
              </button>
            </div>

            {/* Geolocation error */}
            {geoErrorVisible && geoError && (
              <div className="mt-2 text-center text-sm text-red-400 bg-red-900/30 border border-red-700/40 rounded-lg px-4 py-2">
                {geoError}
              </div>
            )}

            {/* Live suggestions dropdown */}
            {showSuggestions && suggestions.length > 0 && (
              <div className="absolute z-50 mt-2 w-full bg-gray-800 border border-gray-700 rounded-xl shadow-2xl overflow-hidden">
                {suggestions.map((band) => (
                  <Link
                    key={band.id}
                    to={`/band/${band.slug}`}
                    onClick={() => setShowSuggestions(false)}
                    className="flex items-center gap-3 px-4 py-3 hover:bg-gray-700 transition"
                  >
                    {band.logo_url ? (
                      <img src={band.logo_url} alt="" className="w-9 h-9 rounded-lg object-cover flex-shrink-0" />
                    ) : (
                      <div className="w-9 h-9 rounded-lg bg-gray-700 flex items-center justify-center flex-shrink-0">
                        <span className="text-gray-400 text-xs font-bold">{getInitials(band.name)}</span>
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-gray-50 text-sm truncate">{band.name}</div>
                      {band.location_name && (
                        <div className="flex items-center gap-1 text-gray-400 text-xs">
                          <MapPin className="w-3 h-3" />
                          {band.location_name}
                        </div>
                      )}
                    </div>
                    {band.genres?.length > 0 && (
                      <span className="text-gray-500 text-[10px] flex-shrink-0">{band.genres[0]}</span>
                    )}
                  </Link>
                ))}
                <Link
                  to={`/search?q=${encodeURIComponent(query)}`}
                  onClick={() => setShowSuggestions(false)}
                  className="flex items-center gap-2 px-4 py-2.5 text-indigo-400 text-sm font-medium hover:bg-gray-700 transition border-t border-gray-700"
                >
                  <Search className="w-4 h-4" />
                  See all results for "{query}"
                </Link>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="max-w-5xl mx-auto px-4 py-16">
        <div className="grid md:grid-cols-3 gap-8">
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-6">
            <Search className="w-10 h-10 text-indigo-400 mb-4" />
            <h3 className="text-xl font-semibold mb-2">Find Local Bands</h3>
            <p className="text-gray-400">
              Search by city, island, or country to discover bands playing near you.
              See their upcoming gigs, setlists, and more.
            </p>
          </div>

          <div className="bg-gray-900 border border-gray-800 rounded-xl p-6">
            <Calendar className="w-10 h-10 text-indigo-400 mb-4" />
            <h3 className="text-xl font-semibold mb-2">Gig Calendar</h3>
            <p className="text-gray-400">
              Browse gigs in list or calendar view — month, week, or day.
              Click any date to see what's playing and where.
            </p>
          </div>

          <div className="bg-gray-900 border border-gray-800 rounded-xl p-6">
            <Users className="w-10 h-10 text-indigo-400 mb-4" />
            <h3 className="text-xl font-semibold mb-2">Band Management</h3>
            <p className="text-gray-400">
              One central place for your band. Manage members, setlists,
              and your public profile. Invite members or let them request to join.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="text-center py-16 px-4">
        <h2 className="text-3xl font-bold mb-4">Ready to get your band online?</h2>
        <p className="text-gray-400 mb-8 max-w-lg mx-auto">
          Create your free account, set up your band page, and start sharing your gigs with the world.
        </p>
        <div className="flex justify-center gap-4">
          <Link
            to="/signup"
            className="bg-indigo-600 hover:bg-indigo-700 text-white px-8 py-3 rounded-lg font-medium transition"
          >
            Get Started Free
          </Link>
          <Link
            to="/search"
            className="bg-gray-800 hover:bg-gray-700 text-gray-50 px-8 py-3 rounded-lg font-medium transition"
          >
            Browse Bands
          </Link>
        </div>
      </section>
    </div>
  );
}
